/**
 * @jsxRuntime classic
 * @jsx jsx
 *
 * MiniAcceptedToast — a compact confirmation shown when the user accepts ONE change
 * while stepping through and more changes remain (so the Review moment stays open
 * and moves to the next segment).
 *
 * It is portalled into the editor's plugin-components container (inside
 * `.ak-editor-content-area`, outside the ProseMirror contentEditable — resolved by
 * `getPopupContainerFromEditorView`) and positioned at the Review moment's captured
 * horizontal center + top edge (see the layout effect for how the captured viewport
 * coordinates are mapped through the container's containing block), so it appears to
 * "slide out" of the top of the toolbar and then fades away — all within ~1s. Its
 * height is half the review toolbar's. The Review moment advances to the next segment
 * `onSlideOut` (a short beat after the mini appears), while the mini keeps fading at
 * the old spot; `onDone` fires when it is fully gone.
 *
 * The centering layer is click-through (`pointer-events: none`). `css`/`keyframes`
 * are co-located with the JSX (required by `@compiled/react`).
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { css, jsx, keyframes } from '@compiled/react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';

import { akEditorFloatingPanelZIndex } from '@atlaskit/editor-shared-styles/consts';
import { token } from '@atlaskit/tokens';

import { renderAcceptedCheckIcon } from './icons';
import { messages } from './messages';

/** Total on-screen lifetime before the mini is fully faded and removed. */
const MINI_ACCEPTED_DURATION_MS = 1000;
/** Delay after the mini appears before the Review moment moves to the next segment. */
const MINI_MOVE_DELAY_MS = 100;
/** Half the review toolbar height (`DEFAULT_REVIEW_MODAL_HEIGHT` in `index.tsx`). */
const MINI_ACCEPTED_HEIGHT = 28;
/** Sit at the same layer the review modal used (see `ModalRegion`). */
const MINI_ACCEPTED_Z_INDEX = Math.min(500, Math.max(402, akEditorFloatingPanelZIndex + 2));

// Emerges from within the toolbar top, slides up just above it, then fades.
const slideOutAndFade = keyframes({
	'0%': { opacity: 0, transform: 'translate(-50%, 0)' },
	'25%': { opacity: 1, transform: 'translate(-50%, -100%)' },
	'70%': { opacity: 1, transform: 'translate(-50%, -100%)' },
	'100%': { opacity: 0, transform: 'translate(-50%, -100%)' },
});

// Fixed anchor point (its `left`/`top` come from the captured toolbar position).
const anchorCSS = css({
	position: 'fixed',
	zIndex: MINI_ACCEPTED_Z_INDEX,
	pointerEvents: 'none',
});

const pillCSS = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: token('space.050'),
	height: MINI_ACCEPTED_HEIGHT,
	boxSizing: 'border-box',
	paddingRight: token('space.150'),
	paddingLeft: token('space.150'),
	backgroundColor: token('elevation.surface.overlay'),
	borderRadius: token('radius.xxlarge', '16px'),
	boxShadow: token('elevation.shadow.overlay'),
	fontWeight: token('font.weight.medium'),
	color: token('color.text'),
	whiteSpace: 'nowrap',
	// Resting position — just above the toolbar (also the static state under reduced motion).
	transform: 'translate(-50%, -100%)',
	animationName: slideOutAndFade,
	animationDuration: `${MINI_ACCEPTED_DURATION_MS}ms`,
	animationTimingFunction: 'ease-out',
	animationFillMode: 'both',
	'@media (prefers-reduced-motion)': {
		animationName: 'none',
	},
});

/**
 * Renders the compact "Accepted" confirmation that slides out of the Review moment.
 *
 * `onSlideOut` fires a short beat after the mini appears (the caller advances the
 * Review moment to the next segment then); `onDone` fires once the mini has fully
 * faded (the caller clears it then).
 */
export function MiniAcceptedToast({
	centerX,
	anchorY,
	container,
	onSlideOut,
	onDone,
}: {
	anchorY: number;
	centerX: number;
	/** Element to portal into — the editor popup container (`getPopupContainerFromEditorView`). */
	container: HTMLElement | null | undefined;
	onDone: () => void;
	onSlideOut: () => void;
}): JSX.Element | null {
	const intl = useIntl();
	const anchorRef = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
	const onSlideOutRef = useRef(onSlideOut);
	onSlideOutRef.current = onSlideOut;
	const onDoneRef = useRef(onDone);
	onDoneRef.current = onDone;

	useEffect(() => {
		const moveTimer = setTimeout(() => onSlideOutRef.current(), MINI_MOVE_DELAY_MS);
		const doneTimer = setTimeout(() => onDoneRef.current(), MINI_ACCEPTED_DURATION_MS);
		return () => {
			clearTimeout(moveTimer);
			clearTimeout(doneTimer);
		};
	}, []);

	// `centerX`/`anchorY` are viewport coordinates, but the portal container lives
	// inside the editor content area which sets `contain`/`container-type` — that
	// makes it (not the viewport) the containing block for `position: fixed`. So we
	// render the anchor at (0,0), measure where that lands (the containing-block
	// origin), and offset it so its top-left sits at the captured viewport point.
	// Works whether or not a containing block is established.
	useLayoutEffect(() => {
		const el = anchorRef.current;
		if (!el) {
			return;
		}
		const rect = el.getBoundingClientRect();
		setPosition({ left: centerX - rect.left, top: anchorY - rect.top });
	}, [centerX, anchorY]);

	const anchorStyle = useMemo(
		() => ({
			left: position ? position.left : 0,
			top: position ? position.top : 0,
			// Hidden until measured so it never flashes at the containing-block origin.
			visibility: position ? ('visible' as const) : ('hidden' as const),
		}),
		[position],
	);

	if (!container) {
		return null;
	}

	return createPortal(
		<div
			ref={anchorRef}
			css={anchorCSS}
			// Coordinates are measured at runtime (see the layout effect above), so they
			// must be applied inline rather than via a static `css()` value.
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- runtime-positioned overlay
			style={anchorStyle}
		>
			<div
				css={pillCSS}
				role="status"
				aria-live="polite"
				data-testid="post-stream-review-mini-accepted-toast"
			>
				{renderAcceptedCheckIcon()}
				<span>{intl.formatMessage(messages.accepted)}</span>
			</div>
		</div>,
		container,
	);
}
